import { z } from 'zod/v4';
import { CaseStatus, CaseType } from '../cases/index.js';
import { DocumentTypes } from '../documents/index.js';
import { PaymentPlanStatus } from '../economy/payment-plans/index.js';
import { DateIntervalSchema, Entity, Gender } from '../index.js';
import { Direction, IncomingState, InteractionType, OutgoingState, } from '../interactions/index.js';
export var FilterType;
(function (FilterType) {
    FilterType["Exact"] = "EXACT";
    FilterType["Containing"] = "CONTAINING";
})(FilterType || (FilterType = {}));
export const caseFilterHasOnlyTenantId = (filter) => {
    if (filter.creditorIds?.length === 0) {
        delete filter.creditorIds;
    }
    const keys = Object.keys(filter);
    return keys.length === 1 && keys[0] === 'tenantId';
};
export const caseFilterHasOnlyTenantIdAndCreditorIds = (filter) => {
    if (filter.creditorIds?.length === 0) {
        delete filter.creditorIds;
    }
    const keys = Object.keys(filter);
    return (keys.length === 2 &&
        keys.every((k) => k === 'tenantId' || k === 'creditorIds'));
};
export const pruneCaseFilter = (filter) => {
    let keys = [
        'hasUpdateIds',
        'companyStatuses',
        'companyBranches',
        'courtIds',
        'companyTypes',
        'doNotHaveUpdateIds',
        'hasCurrentPauseIds',
        'withResponsibleIds',
        'doNotHaveCurrentPauseIds',
        'alternativeWorkflowPaths',
        'documentTypes',
        'economyOnlyVoucherTypeIds',
        'paymentPlansStatuses',
        'withSatisfactions',
        'visitedByUserIds',
        'interactionStates',
        'interactionDirections',
        'interactionTypes',
        'billingModelIds',
        'billingAppendixSetupIds',
        'inWorkflowIds',
        'currentTaskIds',
        'nextTaskIds',
        'caseStatuses',
        'caseTypes',
        'caseEndReasonIds',
        'caseGroups',
        'casesImportsIds',
        'creditorIds',
        'caseSeqIds',
        'caseIds',
    ];
    for (const key of keys) {
        if (Array.isArray(filter[key]) && filter[key].length === 0) {
            delete filter[key];
        }
    }
};
export const caseFilterhash = (filter) => JSON.stringify(Object.entries(filter)
    .filter((d) => typeof d[1] !== 'undefined' &&
    !(Array.isArray(d[1]) && d[1].length === 0))
    .sort((a, b) => a[0].localeCompare(b[0])));
export const CaseFilterSchema = z.object({
    creditorIds: z.array(z.uuid()).optional(),
    // Case
    caseEndReasonIds: z.array(z.uuid()).optional(),
    caseGroups: z.array(z.uuid()).optional(),
    casesImportsIds: z.array(z.uuid()).optional(),
    caseCreationDate: DateIntervalSchema.optional(),
    caseEndedDate: DateIntervalSchema.optional(),
    caseVoucherExpirationDate: DateIntervalSchema.optional(),
    caseSeqIds: z.array(z.number().int()).optional(),
    exceptCaseSeqIds: z.boolean().optional(),
    caseIds: z.array(z.uuid()).optional(),
    caseStatuses: z.array(z.enum(CaseStatus)).optional(),
    caseTypes: z.array(z.enum(CaseType)).optional(),
    hasAlternativeContact: z.boolean().optional(),
    isCompany: z.boolean().optional(),
    activePaymentMethod: z.boolean().optional(),
    // Properties: Should be under case or seperate
    property: z
        .object({
        scope: z.union([
            z.literal('case_groups'),
            z.literal('creditor'),
            z.null(),
        ]),
        // TODO: Remove scopeId compatibility once legacy localStorage filters
        // have been migrated or retired.
        scopeId: z.uuid().optional(),
        scopeIds: z.array(z.uuid()).optional(),
        propertyTypeId: z.uuid().optional(),
        referenceType: z.union([
            z.literal(Entity.Case),
            z.literal(Entity.CaseVoucher),
            z.literal(Entity.Creditor),
            z.literal(Entity.Customer),
        ]),
        handle: z.string(),
        value: z.string(), // Long term multiple types
        commaSeparated: z.boolean(),
    })
        .optional(),
    // Updates
    isPaused: z.boolean().optional(),
    hasUpdateIds: z.array(z.uuid()).optional(),
    doNotHaveUpdateIds: z.array(z.uuid()).optional(),
    hasCurrentPauseIds: z.array(z.uuid()).optional(),
    withResponsibleIds: z.array(z.union([z.uuid(), z.null()])).optional(),
    doNotHaveCurrentPauseIds: z.array(z.uuid()).optional(),
    // Economy
    economyMinBalance: z.number().optional(),
    economyMaxBalance: z.number().optional(),
    economyOnlyVoucherTypeIds: z.array(z.uuid()).optional(),
    billingModelIds: z.array(z.uuid()).optional(),
    billingAppendixSetupIds: z.array(z.uuid()).optional(),
    // Workflows
    inWorkflowIds: z.array(z.uuid()).optional(),
    currentTaskIds: z.array(z.uuid()).optional(),
    nextTaskIds: z.array(z.uuid()).optional(),
    noNextTask: z.boolean().optional(),
    alternativeWorkflowPaths: z.array(z.string()).optional(),
    // Documents
    documentTypes: z.array(z.enum(DocumentTypes)).optional(),
    documentTypesIsSigned: z.boolean().optional(),
    hasFoundation: z.boolean().optional(),
    // Payment Plans
    paymentPlansStatuses: z.array(z.enum(PaymentPlanStatus)).optional(),
    isNotHavingTheAbovePaymentPlansStatuses: z.boolean().optional(),
    // Interactions
    interactionStates: z
        .array(z.union([z.enum(OutgoingState), z.enum(IncomingState)]))
        .optional(),
    interactionDirections: z.array(z.enum(Direction)).optional(),
    interactionTypes: z.array(z.enum(InteractionType)).optional(),
    noneOfTheAboveInteractions: z.boolean().optional(),
    // Debtor
    maxRkiRegistrations: z.number().optional(),
    minRkiRegistrations: z.number().optional(),
    isReportedByCollector: z.boolean().optional(),
    companyTypes: z.array(z.string()).optional(),
    companyStatuses: z.array(z.string()).optional(),
    companyBranches: z.array(z.string()).optional(),
    gender: z.enum(Gender).optional(),
    zipcodes: z.array(z.number()).optional(),
    courtIds: z.array(z.string()).optional(),
    hasAddress: z.boolean().optional(),
    hasPhone: z.boolean().optional(),
    hasEmail: z.boolean().optional(),
    withSatisfactions: z.array(z.number()).optional(),
    // Accounts
    visitedByUserIds: z.array(z.uuid()).optional(),
    visitedAt: DateIntervalSchema.optional(),
    // Pagination
    offset: z.number().optional(),
    limit: z.number().optional(),
});
//# sourceMappingURL=index.js.map